import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { API } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = password.length > 0 && confirm.length > 0 && password !== confirm;
  const match = password.length >= 8 && confirm.length > 0 && password === confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("This reset link is missing its token"); return; }
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      toast.success("Password updated. Please sign in with your new password.");
      nav("/login", { replace: true });
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const detail = typeof raw === "string" ? raw : (Array.isArray(raw) ? (raw[0]?.msg || "") : "");
      toast.error(detail || "Failed to reset password. Please request a new link.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 mb-8" data-testid="reset-brand">
          <TrendingUp className="w-5 h-5 text-blue-500" />
          <span className="font-heading font-semibold text-lg tracking-tight">ZigZagX</span>
        </Link>

        <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-6" data-testid="reset-password-card">
          {!token ? (
            <div className="space-y-3" data-testid="reset-invalid">
              <h1 className="font-heading text-xl uppercase tracking-wider text-rose-400">Invalid link</h1>
              <p className="text-sm text-zinc-400">This password-reset link is missing or malformed.</p>
              <Link to="/forgot-password">
                <Button className="mt-2 rounded-sm bg-blue-600 hover:bg-blue-500 h-9" data-testid="reset-request-new">Request a new link</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h1 className="font-heading text-xl uppercase tracking-wider text-zinc-100 mb-1">Choose a new password</h1>
                <p className="text-sm text-zinc-500 leading-relaxed">
                  Set a new password to finish signing back in. Links expire 30 minutes after they're generated.
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">New password</Label>
                <PasswordInput autoFocus value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Confirm password</Label>
                <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
              </div>
              {tooShort && <p className="text-[11px] text-amber-400 font-mono" data-testid="reset-hint">Minimum 8 characters.</p>}
              {mismatch && <p className="text-[11px] text-rose-400 font-mono" data-testid="reset-hint">Passwords do not match.</p>}
              {match && <p className="text-[11px] text-emerald-400 font-mono" data-testid="reset-hint">Passwords match.</p>}
              <Button type="submit" disabled={busy || !match} data-testid="reset-submit-btn" className="w-full rounded-sm bg-blue-600 hover:bg-blue-500 h-10 disabled:opacity-50">
                {busy ? "Saving…" : "Set new password"}
              </Button>
              <div className="text-center pt-1">
                <Link to="/login" className="text-xs text-zinc-500 hover:text-zinc-300 uppercase tracking-wider" data-testid="reset-back-link">
                  ← Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
